/*  Shared  */
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/*  React  */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Hook to fetch the /{provider}/groups data async
export const useFetchProviderGroups = ({
    accessToken,
    applicationId,
    applicationSecret,
    clientId,
    identityTenantId,
    providerRoute,
    useAsmAppCredentials
}) => {
    const { t } = useTranslation();

    const [providerGroups, setProviderGroups] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        let orgUnitsResponse;

        const fetchData = async () => {
            if (!clientId || !providerRoute) {
                setLoading(false);
                return;
            }
            const params = {
                accessToken,
                applicationId,
                applicationSecret,
                identityTenantId,
                includeClients: true,
                platformTenantId: clientId,
                useAsmAppCredentials
            };

            try {
                setLoading(true);
                const groupsResponse = await axios.get(
                    `/${providerRoute}/groups`,
                    {
                        cancelToken: source.token,
                        params
                    }
                );
                if (providerRoute === 'google') {
                    orgUnitsResponse = await axios.get(
                        `/${providerRoute}/orgUnits`,
                        {
                            cancelToken: source.token,
                            params
                        }
                    );
                }
                if (
                    groupsResponse.data?.data?.groups?.length === 0 ||
                    (providerRoute === 'google' &&
                        orgUnitsResponse?.data?.data?.groups?.length)
                ) {
                    debug.e(t('Provider.Groups.notFound.message'));
                } else {
                    let returnGroups = groupsResponse.data?.data?.groups ?? [];

                    if (orgUnitsResponse?.data?.data?.orgUnits?.length) {
                        const { orgUnits } = orgUnitsResponse.data.data ?? [];
                        returnGroups = [...returnGroups, ...orgUnits];
                    }
                    setProviderGroups(returnGroups);
                }
            } catch (error) {
                if (axios.isCancel(error)) {
                    // Request was canceled, do nothing
                } else {
                    debug.e(t('Provider.Groups.error.message'), error);
                }
            }
            setLoading(false);
        };

        fetchData();

        return () => {
            // Cancel the request when the component unmounts
            source.cancel();
        };
    }, [
        accessToken,
        applicationId,
        applicationSecret,
        clientId,
        identityTenantId,
        providerRoute,
        t,
        useAsmAppCredentials
    ]);

    return {
        loading,
        providerGroups
    };
};

export const useFetchProviderGroupsUsers = ({
    accessToken,
    applicationId,
    applicationSecret,
    clientId,
    id,
    identityTenantId,
    isOrgUnit,
    providerRoute,
    useAsmAppCredentials
}) => {
    const { t } = useTranslation();

    const [providerGroupsUsers, setProviderGroupsUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const currentRequestRef = useRef(null);

    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();
        currentRequestRef.current = source;

        const fetchData = async () => {
            if (!clientId || !providerRoute || !id) {
                setLoading(false);
                return;
            }
            const params = {
                accessToken,
                applicationId,
                applicationSecret,
                identityTenantId,
                includeClients: true,
                isOrgUnit,
                platformTenantId: clientId,
                useAsmAppCredentials
            };

            try {
                setLoading(true);
                const response = await axios.get(
                    `/${providerRoute}/tenantGroup/${id}`,
                    {
                        cancelToken: source.token,
                        params
                    }
                );

                setProviderGroupsUsers(response?.data?.data?.users || []);
            } catch (error) {
                if (axios.isCancel(error)) {
                    // Request was canceled, do nothing
                } else {
                    debug.e(t('Provider.Groups.usersNotFound.message'), error);
                }
            } finally {
                // Only set loading to false if the current request is the same as the one that was just completed
                if (currentRequestRef.current === source) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            // Cancel the request when the component unmounts
            source.cancel();
        };
    }, [
        accessToken,
        applicationId,
        applicationSecret,
        clientId,
        id,
        identityTenantId,
        isOrgUnit,
        providerRoute,
        t,
        useAsmAppCredentials
    ]);

    return {
        loading,
        providerGroupsUsers
    };
};
